import { Role, type User } from "@cryptors/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { IResourceComponentsProps, HttpError } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";

import { useGetAvatars } from "@/api/avatar.api";
import StyledSelect from "@/components/forms/StyledSelect";
import UploadField from "@/components/forms/UploadField";

import ClientAccessFields from "./components/ClientAccessFields";
import UserCryptorDraftAccessSelect from "./components/UserCryptorDraftAccessSelect";
import { userFormSchema, UserFormValues } from "./user-form";

// Normalises allowedCities ([{id}] from the API or number[] from the picker).
const toCityIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === "number" ? v : (v as { id?: number })?.id))
    .filter((v): v is number => typeof v === "number");
};

export const UserEdit: React.FC<IResourceComponentsProps> = () => {
  const { data: avatars } = useGetAvatars();

  const {
    register,
    control,
    formState: { errors, isValid },
    handleSubmit,
    setError,
    refineCore: { formLoading, onFinish },
    watch,
  } = useForm<User, HttpError, UserFormValues>({
    resolver: zodResolver(userFormSchema),
    mode: "onChange",
    defaultValues: {
      pseudo: "",
      email: "",
      emailConfirmed: false,
      role: Role.USER,
      avatarId: 0,
      cryptorsDraftAccess: [],
      isTesterAccount: false,
      allowedCities: [],
      logoUrl: null,
    },
  });

  const onFinishHandler = async (data: UserFormValues) => {
    await onFinish({
      pseudo: data.pseudo,
      email: data.email,
      emailConfirmed: data.emailConfirmed,
      role: data.role,
      avatarId: data.avatarId,
      cryptorsDraftAccess: data.cryptorsDraftAccess.map((entry) =>
        typeof entry === "number" ? entry : entry.id,
      ),
      isTesterAccount: data.isTesterAccount,
      allowedCities: toCityIds(data.allowedCities),
      logoUrl: data.logoUrl ?? null,
    });
  };

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("pseudo", {
            required: "Ce champ est requis",
          })}
          error={!!errors.pseudo}
          helperText={errors.pseudo?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Pseudo"
          name="pseudo"
        />
        <TextField
          {...register("email", {
            required: "Ce champ est requis",
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email"
          name="email"
        />
        <Controller
          control={control}
          name="emailConfirmed"
          render={({ field }) => (
            <FormControlLabel
              label="Email confirmé"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
            />
          )}
        />
        <Controller
          control={control}
          name="isTesterAccount"
          render={({ field }) => (
            <FormControlLabel
              label="Est un testeur"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
            />
          )}
        />
        <Controller
          control={control}
          name="role"
          render={({ field, fieldState }) => (
            <StyledSelect
              label="Role"
              onChange={field.onChange}
              onBlur={field.onBlur}
              value={field.value}
              error={fieldState.invalid}
            >
              <MenuItem value={Role.USER}>User</MenuItem>
              <MenuItem value={Role.ADMIN}>Admin</MenuItem>
              <MenuItem value={Role.CLIENT}>Client</MenuItem>
              <MenuItem value={Role.REDACTEUR}>Rédacteur</MenuItem>
              <MenuItem value={Role.ANALYSTE}>Analyste</MenuItem>
            </StyledSelect>
          )}
        />
        <Controller
          control={control}
          name="avatarId"
          render={({ field, fieldState }) => (
            <StyledSelect
              label="Avatar"
              onChange={field.onChange}
              onBlur={field.onBlur}
              value={field.value}
              error={fieldState.invalid}
            >
              {avatars.map((avatar) => (
                <MenuItem key={avatar.id} value={avatar.id}>
                  <img alt="avatar" src={avatar.image.url} width={35} />
                </MenuItem>
              ))}
            </StyledSelect>
          )}
        />
        {watch("role") !== Role.ADMIN && watch("role") !== Role.CLIENT && (
          <UserCryptorDraftAccessSelect control={control} />
        )}
        {watch("role") === Role.CLIENT && (
          <>
            <ClientAccessFields control={control} />
            <UploadField
              name="logoUrl"
              uploadButtonLabel="Télécharger le logo client"
              s3FoldersType="client-logos"
              accept="image/*"
              control={control}
              setError={setError}
              optional
            />
          </>
        )}
      </Box>
    </Edit>
  );
};
